import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Label } from "./ui/label";
import { Input } from "./ui/input";

const PARTS = [
  { key: "address_street", label: "Indirizzo", placeholder: "Via Roma", span: "sm:col-span-4" },
  { key: "address_number", label: "Numero", placeholder: "12", span: "sm:col-span-2" },
  { key: "address_zip", label: "CAP", placeholder: "00100", span: "sm:col-span-2", inputMode: "numeric", maxLength: 10 },
  { key: "address_city", label: "Città", placeholder: "Roma", span: "sm:col-span-3" },
  { key: "address_province", label: "Provincia", placeholder: "RM", span: "sm:col-span-1", maxLength: 4, upper: true },
];

export const addressPartsFrom = (src = {}) => ({
  address_street: src.address_street ?? src.address ?? "",
  address_number: src.address_number || "",
  address_zip: src.address_zip || "",
  address_city: src.address_city || "",
  address_province: src.address_province || "",
});

export const composeAddress = (a) => {
  const street = [a.address_street, a.address_number].map((x) => (x || "").trim()).filter(Boolean).join(" ");
  let zipCity = [a.address_zip, a.address_city].map((x) => (x || "").trim()).filter(Boolean).join(" ");
  const prov = (a.address_province || "").trim().toUpperCase();
  if (prov) zipCity = zipCity ? `${zipCity} (${prov})` : prov;
  return [street, zipCity].filter(Boolean).join(", ");
};

function CapChoices({ options, current, onPick }) {
  if (options.length < 2) return null;
  return (
    <div data-testid="cap-choices" className="col-span-2 sm:col-span-6 -mt-2 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-slate-500">Comuni con questo CAP:</span>
      {options.map((o) => (
        <button key={`${o.city}-${o.province}`} type="button" onClick={() => onPick(o)}
          data-testid={`cap-choice-${o.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-full border px-2.5 py-0.5 transition-colors ${current === o.city ? "border-primary bg-primary/10 text-primary" : "border-slate-200 hover:bg-slate-50"}`}>
          {o.city} ({o.province})
        </button>
      ))}
    </div>
  );
}

function useCapLookup(zip, typedZip, apply) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    const cap = (zip || "").trim();
    if (!/^\d{5}$/.test(cap)) { setOptions([]); return; }
    let alive = true;
    api.get(`/cap/${cap}`).then(({ data }) => {
      if (!alive) return;
      setOptions(data.comuni);
      if (typedZip.current === cap && data.comuni.length) {
        typedZip.current = null;
        apply(data.comuni[0]);
      }
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);
  return options;
}

export default function AddressFields({ value, onChange, testidPrefix }) {
  const parts = addressPartsFrom(value);
  const latest = useRef(parts);
  latest.current = parts;
  const applyPatch = (patch) => {
    // write all parts so a missing address_street never falls back to the composed `address`
    const next = { ...latest.current, ...patch };
    Object.entries(next).forEach(([k, v]) => onChange(k, v));
    onChange("address", composeAddress(next));
  };
  const pickComune = (o) => applyPatch({ address_city: o.city, address_province: o.province });
  const typedZip = useRef(null);
  const capOptions = useCapLookup(parts.address_zip, typedZip, pickComune);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-4" data-testid={`${testidPrefix}-address-fields`}>
      {PARTS.map((f) => (
        <div key={f.key} className={`space-y-1.5 min-w-0 ${f.key === "address_street" ? "col-span-2" : ""} ${f.span}`}>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</Label>
          <Input
            data-testid={`${testidPrefix}-${f.key.replace("address_", "address-")}`}
            value={parts[f.key]}
            placeholder={f.placeholder}
            inputMode={f.inputMode}
            maxLength={f.maxLength}
            onChange={(e) => {
              const v = f.upper ? e.target.value.toUpperCase() : e.target.value;
              if (f.key === "address_zip") typedZip.current = v.trim();
              applyPatch({ [f.key]: v });
            }}
          />
        </div>
      ))}
      <CapChoices options={capOptions} current={parts.address_city} onPick={pickComune} />
    </div>
  );
}
